import React, { createContext, useContext, useEffect, useState } from "react";
import client, { setTenantContext } from "../lib/api";

const AuthContext = createContext(null);
export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  // For super_admin: currently selected tenant id to operate on
  const [activeTenantId, setActiveTenantId] = useState(
    localStorage.getItem("pm_active_tenant") || null
  );

  const isSuper = user?.role === "super_admin";

  useEffect(() => {
    setTenantContext(activeTenantId, isSuper);
  }, [activeTenantId, isSuper]);

  const bootstrap = async () => {
    const token = localStorage.getItem("pm_token");
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await client.get("/auth/me");
      setUser(data.user);
      setTenant(data.tenant);
    } catch (e) {
      localStorage.removeItem("pm_token");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { bootstrap(); }, []);

  const login = async (email, password) => {
    const { data } = await client.post("/auth/login", { email, password });
    localStorage.setItem("pm_token", data.token);
    setUser(data.user);
    setTenant(data.tenant);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem("pm_token");
    localStorage.removeItem("pm_active_tenant");
    setUser(null);
    setTenant(null);
    window.location.href = "/login";
  };

  // Clears the session WITHOUT navigating away (lets caller control UX / show toasts).
  const clearSession = () => {
    localStorage.removeItem("pm_token");
    localStorage.removeItem("pm_active_tenant");
    setUser(null);
    setTenant(null);
    setActiveTenantId(null);
  };

  const selectTenant = (id) => {
    setActiveTenantId(id);
    if (id) localStorage.setItem("pm_active_tenant", id);
    else localStorage.removeItem("pm_active_tenant");
  };

  const can = (minRole) => {
    const levels = { viewer: 1, connector_admin: 2, analyst: 3, admin: 4, super_admin: 5 };
    return (levels[user?.role] || 0) >= (levels[minRole] || 0);
  };

  return (
    <AuthContext.Provider value={{ user, tenant, loading, isSuper, activeTenantId, login, logout, clearSession, selectTenant, can }}>
      {children}
    </AuthContext.Provider>
  );
}
